import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { searchGlobalContacts } from '../../services/api/implementation/impl';
import searchIcon from '../../../shared/assets/svg/search.svg';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 320;
const PANEL_WIDTH = 352;

function formatLeadPhone(lead) {
  const phone = lead?.user?.phone_number || lead?.phone_number || '';
  const cc = lead?.user?.country_code || lead?.country_code || '';
  if (!phone) return '';
  return cc ? `${cc} ${phone}` : String(phone);
}

function formatPreleadPhone(prelead) {
  const phone = prelead?.phone || '';
  const cc = prelead?.countryCode || prelead?.country_code || '';
  if (!phone) return '';
  return cc ? `${cc} ${phone}` : String(phone);
}

function getLeadDisplayName(lead) {
  return lead?.user?.name || lead?.name || 'Unnamed lead';
}

function getPreleadDisplayName(prelead) {
  return prelead?.name || 'Unnamed prelead';
}

function getPreleadListId(prelead) {
  return (
    prelead?.pre_lead_list_id ||
    prelead?.pre_lead_list?.id ||
    prelead?.list_id ||
    null
  );
}

function getConvertedLeadId(prelead) {
  return prelead?.lead?.id || prelead?.lead_id || null;
}

/**
 * Header-only global contact search across leads + preleads.
 * Intentionally isolated from SalesList / Prelead list URL search params.
 */
const SalesGlobalContactSearch = () => {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const requestIdRef = useRef(0);

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [preleads, setPreleads] = useState([]);
  const [error, setError] = useState('');
  const [panelStyle, setPanelStyle] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const updatePanelPosition = useCallback(() => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.min(PANEL_WIDTH, window.innerWidth - 16);
    const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
    setPanelStyle({
      position: 'fixed',
      top: rect.bottom + 6,
      left,
      width
    });
  }, []);

  useLayoutEffect(() => {
    if (!open || !query.trim()) {
      setPanelStyle(null);
      return undefined;
    }
    updatePanelPosition();
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);
    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [open, query, updatePanelPosition]);

  useEffect(() => {
    const onPointerDown = (event) => {
      const inRoot = rootRef.current?.contains(event.target);
      const inPanel = panelRef.current?.contains(event.target);
      if (!inRoot && !inPanel) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  useEffect(() => {
    if (debouncedQuery.length < MIN_QUERY_LENGTH) {
      setLeads([]);
      setPreleads([]);
      setLoading(false);
      setError('');
      return undefined;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError('');

    searchGlobalContacts(debouncedQuery)
      .then((result) => {
        if (requestId !== requestIdRef.current) return;
        setLeads(result.leads || []);
        setPreleads(result.preleads || []);
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        setLeads([]);
        setPreleads([]);
        setError('Could not search contacts. Try again.');
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      });

    return undefined;
  }, [debouncedQuery]);

  const closeAndClear = useCallback(() => {
    setOpen(false);
    setQuery('');
    setDebouncedQuery('');
    setLeads([]);
    setPreleads([]);
    setError('');
  }, []);

  const handleSelectLead = useCallback(
    (lead) => {
      if (!lead?.id) return;
      closeAndClear();
      navigate(`/builder/sales/${lead.id}`);
    },
    [closeAndClear, navigate]
  );

  const handleSelectPrelead = useCallback(
    (prelead) => {
      const convertedLeadId = getConvertedLeadId(prelead);
      if (convertedLeadId) {
        closeAndClear();
        navigate(`/builder/sales/${convertedLeadId}`);
        return;
      }

      const listId = getPreleadListId(prelead);
      const search = encodeURIComponent(debouncedQuery || query || '');
      closeAndClear();

      if (listId) {
        navigate(`/builder/sales/prelead-lists/${listId}?search=${search}`);
        return;
      }
      navigate(`/builder/sales/?view=leads&tab=${encodeURIComponent('Prelead List')}`);
    },
    [closeAndClear, debouncedQuery, navigate, query]
  );

  const hasQuery = query.trim().length > 0;
  const showPanel = open && hasQuery;
  const empty =
    !loading &&
    !error &&
    debouncedQuery.length >= MIN_QUERY_LENGTH &&
    leads.length === 0 &&
    preleads.length === 0;

  return (
    <div ref={rootRef} className="relative shrink-0">
      <div className="flex h-9 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 shadow-sm">
        <img src={searchIcon} alt="" className="h-3.5 w-3.5 opacity-60" />
        <input
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setOpen(false);
            }
          }}
          placeholder="Find contact…"
          aria-label="Search contacts across leads and preleads"
          className="w-[10.5rem] bg-transparent text-sm text-gray-800 outline-none placeholder:text-gray-400 sm:w-[13rem]"
        />
      </div>

      {showPanel && panelStyle
        ? createPortal(
            <div
              ref={panelRef}
              style={panelStyle}
              className="z-[10050] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
              {query.trim().length < MIN_QUERY_LENGTH ? (
                <p className="px-3 py-2.5 text-xs text-gray-500">Type at least 2 characters</p>
              ) : loading ? (
                <p className="px-3 py-2.5 text-xs text-gray-500">Searching…</p>
              ) : error ? (
                <p className="px-3 py-2.5 text-xs text-red-600">{error}</p>
              ) : empty ? (
                <p className="px-3 py-2.5 text-xs text-gray-500">No contacts found</p>
              ) : (
                <div className="max-h-80 overflow-y-auto py-1">
                  {leads.length > 0 ? (
                    <div>
                      <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                        Leads
                      </div>
                      {leads.map((lead) => {
                        const phone = formatLeadPhone(lead);
                        const project = lead?.projects?.[0]?.name || lead?.project?.name || '';
                        return (
                          <button
                            key={`lead-${lead.id}`}
                            type="button"
                            onClick={() => handleSelectLead(lead)}
                            className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-gray-50">
                            <span className="text-sm font-medium text-gray-900">
                              {getLeadDisplayName(lead)}
                            </span>
                            <span className="text-xs text-gray-500">
                              {[phone, project, lead?.pstatus].filter(Boolean).join(' · ')}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : null}

                  {preleads.length > 0 ? (
                    <div>
                      <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                        Preleads
                      </div>
                      {preleads.map((prelead) => {
                        const phone = formatPreleadPhone(prelead);
                        const listName = prelead?.pre_lead_list?.name || '';
                        const converted = Boolean(getConvertedLeadId(prelead));
                        return (
                          <button
                            key={`prelead-${prelead.id}`}
                            type="button"
                            onClick={() => handleSelectPrelead(prelead)}
                            className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-gray-50">
                            <span className="text-sm font-medium text-gray-900">
                              {getPreleadDisplayName(prelead)}
                              {converted ? (
                                <span className="ml-1.5 text-[10px] font-normal text-[#2A565E]">
                                  (lead)
                                </span>
                              ) : null}
                            </span>
                            <span className="text-xs text-gray-500">
                              {[phone, listName, prelead?.state].filter(Boolean).join(' · ')}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              )}
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default SalesGlobalContactSearch;
